import { useState } from "react";
import { ApiError, post, type ActionDetail, type BoardRow } from "../api";
import { isWithheldText, wordsOf } from "../api";
import { label } from "../components/Money";
import { Loading, Refused } from "../components/Shell";
import { toneFor } from "../components/tone";
import { useApi } from "../useApi";

interface Payload {
  open: BoardRow[];
  done: BoardRow[];
  actions: Record<string, ActionDetail>;
  writeback: string[];
}

export function Board() {
  const { data, error, loading, reload } = useApi<Payload>("/board");
  const [focus, setFocus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;

  const selectedId = focus ?? data.open[0]?.id ?? data.done[0]?.id ?? null;
  const selected = selectedId ? data.actions[selectedId] : null;

  const groups = new Map<number, BoardRow[]>();
  for (const row of data.open) {
    groups.set(row.priority, [...(groups.get(row.priority) ?? []), row]);
  }

  async function act(path: string, payload: object) {
    setBusy(true);
    setProblem(null);
    try {
      await post(path, payload);
      reload();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.detail : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Two different objects share this board: a clock with a drafted letter,
  // and a note somebody wrote in a meeting. The clause is what tells them
  // apart, and it is what a reader needs to know carries contractual weight.
  const drafted = data.open.filter((r) => r.clause && r.has_draft).length;
  const fromMinutes = data.open.filter((r) => !r.clause).length;

  return (
    <>
      <h1>Follow-up board</h1>
      {/* This said "each with an owner and a clause. Every one has a reply
          drafted", which was false for half the board: the items lifted from
          meeting minutes carry neither. They are a different kind of thing
          from a contractual clock, so the count says which is which rather
          than making one claim that fits neither. */}
      <p className="standfirst">
        {drafted > 0 && (
          <>
            {drafted} {drafted === 1 ? "reply" : "replies"} drafted against a
            clause — every one still needs a person to send it.
          </>
        )}
        {drafted > 0 && fromMinutes > 0 && " "}
        {fromMinutes > 0 && (
          <>
            {fromMinutes} {fromMinutes === 1 ? "item" : "items"} from the
            minutes, with no clause and no letter.
          </>
        )}
      </p>

      <div className="split">
        <div>
          {[...groups.entries()].sort(([a], [b]) => a - b).map(([priority, rows]) => (
            <section className="prigroup" key={priority}>
              <header>
                <span className={`pri p${priority}`}>Priority {priority}</span>
                <span className="rulel" />
                <span className="mn withheld">{rows.length}</span>
              </header>
              {rows.map((row) => (
                /* The board ranks the same way the radar does. An item whose
                   clock is running towards an outcome that happens on its
                   own is marked; one whose outcome has already taken effect
                   is quietened, because it is a record rather than work. */
                <button key={row.id} type="button"
                  className={`actionrow actionrow--${toneFor(row)}`
                    + (row.id === selectedId ? " on" : "")}
                  onClick={() => setFocus(row.id)}>
                  <span className="title">{row.title}</span>
                  <span className="withheld">{row.owner}</span>
                  <span className="mn" style={{ textAlign: "right" }}>{row.due_date}</span>
                </button>
              ))}
            </section>
          ))}

          {data.done.length > 0 && (
            <section className="prigroup">
              <header>
                <span className="pri done">Done</span>
                <span className="rulel" />
                <span className="mn withheld">{data.done.length}</span>
              </header>
              {data.done.map((row) => (
                <button key={row.id} type="button"
                  className={`actionrow muted${row.id === selectedId ? " on" : ""}`}
                  onClick={() => setFocus(row.id)}>
                  <span className="title">{row.title}</span>
                  <span className="withheld">{label(row.status)}</span>
                  <span className="mn" style={{ textAlign: "right" }}>{row.due_date}</span>
                </button>
              ))}
            </section>
          )}
        </div>

        <aside>
          {!selected ? (
            <section className="card"><p className="empty">Select an action to read its drafted reply.</p></section>
          ) : (
            <section className="card">
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
                <span className="k">Drafted reply</span>
                {/* Drafted and waiting for a person: that is the whole
                    point of the board, so it is the one thing marked. */}
                <span className="pill act">{label(selected.confirmation)}</span>
              </div>
              <h2 className="ser" style={{ fontSize: 18, fontWeight: 500, margin: "0 0 12px" }}>
                {wordsOf(selected.draft_subject) || selected.title}
              </h2>
              {isWithheldText(selected.draft_body) ? (
                <div className="note">
                  <span className="h">Being drafted by the other side</span>
                  An unsent letter is its author's side's position, not the record.
                  You can see that a reply is being prepared and who owes it; the
                  words are theirs until they send it.
                </div>
              ) : (
                <pre className="letter">{wordsOf(selected.draft_body) || selected.detail}</pre>
              )}

              <div className="note">
                <span className="h">Nothing is sent automatically</span>
                Neev drafts; a person completes and sends it from their own mail client.
              </div>

              {data.writeback.length > 0 && (
                <WriteBack types={data.writeback} actionId={selected.id}
                  busy={busy} onRequest={(type) =>
                    act("/approvals/request", { action_id: selected.id, write_type: type })} />
              )}

              <div style={{ display: "flex", gap: 10, alignItems: "center",
                            justifyContent: "space-between", marginTop: 18, flexWrap: "wrap" }}>
                <span className="mn withheld" style={{ fontSize: 12 }}>
                  owner {selected.owner || "—"} · due {selected.due_date ?? "—"} ·{" "}
                  <span className="pill">{label(selected.status)}</span>
                </span>
                {selected.may_complete ? (
                  <span style={{ display: "flex", gap: 8 }}>
                    {wordsOf(selected.draft_body) && (
                      <a className="btn" href={`mailto:?subject=${encodeURIComponent(wordsOf(selected.draft_subject) ?? "")}&body=${encodeURIComponent(wordsOf(selected.draft_body) ?? "")}`}>
                        Open in mail
                      </a>
                    )}
                    {["sent", "closed"].includes(selected.status) ? (
                      <button className="btn" disabled={busy}
                        onClick={() => act(`/board/${selected.id}/status`, { status: "reopen" })}>
                        Reopen
                      </button>
                    ) : (
                      <button className="primary" disabled={busy}
                        onClick={() => act(`/board/${selected.id}/status`,
                          { status: wordsOf(selected.draft_body) ? "sent" : "closed" })}>
                        {wordsOf(selected.draft_body) ? "Mark as sent" : "Mark as done"}
                      </button>
                    )}
                  </span>
                ) : (
                  <span className="withheld" style={{ fontSize: 12 }}>
                    {selected.owner || "the responsible party"} must send this
                  </span>
                )}
              </div>
              {problem && <div className="err">{problem}</div>}
            </section>
          )}
        </aside>
      </div>
    </>
  );
}

function WriteBack({ types, busy, onRequest }: {
  types: string[]; actionId: string; busy: boolean;
  onRequest: (type: string) => void;
}) {
  const [type, setType] = useState(types[0] ?? "");
  return (
    <div className="card" style={{ background: "var(--sunken)" }}>
      <label className="k" htmlFor="wb">Write this back to the register</label>
      <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        <select id="wb" value={type} onChange={(e) => setType(e.target.value)}
          style={{ font: "inherit", padding: "8px 10px", borderRadius: 8,
                   border: "1px solid var(--rule)" }}>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button className="btn" disabled={busy} onClick={() => onRequest(type)}>
          Request approval
        </button>
      </div>
      <p className="hint">
        This does not write anything. It asks a second person to authorise this exact text.
      </p>
    </div>
  );
}
